import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { KYCWizard } from "@/components/kyc/KYCWizard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Users, ArrowRight, Upload, LayoutDashboard } from "lucide-react";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";

interface Family {
  id: string;
  name: string;
  org_id: string;
  onboarding_status: string | null;
}

export default function FamilyKYC() {
  const { familyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { orgId } = useTenant();
  const token = searchParams.get("token");

  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [resolvedFamilyId, setResolvedFamilyId] = useState<string | null>(familyId || null);
  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(orgId || null);
  const [kycJustCompleted, setKycJustCompleted] = useState(false);

  // If using token, resolve family from invitation
  const { data: invitation, isLoading: inviteLoading } = useQuery({
    queryKey: ["kyc-invitation", token],
    queryFn: async () => {
      if (!token) return null;

      const { data, error } = await supabase
        .from("family_invitations")
        .select(`
          id,
          family_id,
          org_id,
          email,
          accepted_at,
          expires_at,
          families:family_id (
            id,
            name,
            onboarding_status
          )
        `)
        .eq("token", token)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!token,
  });

  // Check authorization
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Not logged in - redirect to auth with return URL
        const returnUrl = window.location.pathname + window.location.search;
        navigate(`/auth?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }

      // If using token-based access
      if (token && invitation) {
        // Check if user email matches invitation
        if (user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
          toast({
            title: "Wrong account",
            description: `Please sign in with ${invitation.email} to access this KYC form.`,
            variant: "destructive",
          });
          setIsAuthorized(false);
          return;
        }

        // Check if invitation is accepted
        if (!invitation.accepted_at) {
          toast({
            title: "Invitation not accepted",
            description: "Please accept the invitation first.",
            variant: "destructive",
          });
          setIsAuthorized(false);
          return;
        }

        // Check if expired
        if (new Date(invitation.expires_at) < new Date()) {
          toast({
            title: "Link expired",
            description: "This KYC link has expired.",
            variant: "destructive",
          });
          setIsAuthorized(false);
          return;
        }

        setResolvedFamilyId(invitation.family_id);
        setResolvedOrgId(invitation.org_id);
        setIsAuthorized(true);
        return;
      }

      // If using direct familyId access (for MFO staff or authenticated family users)
      if (familyId) {
        // Check if user has access to this family
        const { data: familyRole } = await supabase
          .from("users_family_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("family_id", familyId)
          .maybeSingle();

        const { data: orgRole } = await supabase
          .from("users_org_roles")
          .select("role, org_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (familyRole || orgRole) {
          setResolvedFamilyId(familyId);
          if (orgRole) {
            setResolvedOrgId(orgRole.org_id);
          }
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
        return;
      }

      // No token or familyId - unauthorized
      setIsAuthorized(false);
    };

    if (!inviteLoading) {
      checkAuth();
    }
  }, [token, invitation, familyId, inviteLoading, navigate, toast]);

  // Fetch family details
  const { data: family, isLoading: familyLoading } = useQuery({
    queryKey: ["family-for-kyc", resolvedFamilyId],
    queryFn: async () => {
      if (!resolvedFamilyId) return null;

      const { data, error } = await supabase
        .from("families")
        .select("id, name, org_id, onboarding_status")
        .eq("id", resolvedFamilyId)
        .single();

      if (error) throw error;
      return data as Family;
    },
    enabled: !!resolvedFamilyId && isAuthorized === true,
  });

  const handleComplete = () => {
    // Show the completion screen with next steps instead of navigating away
    setKycJustCompleted(true);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Loading states
  if (inviteLoading || isAuthorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not authorized
  if (isAuthorized === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this KYC form. Please ensure you're signed in with the correct account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // KYC already completed
  if (family?.onboarding_status === "active" || family?.onboarding_status === "review") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle>KYC Already Complete</CardTitle>
            <CardDescription>
              KYC information has already been submitted for {family.name}.
              {family.onboarding_status === "review" && " Your submission is under review."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading family
  if (familyLoading || !family) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // KYC just completed - show success screen with next steps
  if (kycJustCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl">KYC Complete!</CardTitle>
            <CardDescription className="text-base">
              Thank you for completing the KYC information for {family.name}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                What's Next?
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Import your portfolio data</p>
                    <p className="text-sm text-muted-foreground">
                      Upload statements from your custodians and banks to automatically populate your portfolio.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Start tracking your investments</p>
                    <p className="text-sm text-muted-foreground">
                      View your consolidated portfolio, track performance, and manage documents.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full"
              >
                <LayoutDashboard className="h-4 w-4" />
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate("/data/ingestion")}
                className="w-full"
              >
                <Upload className="h-4 w-4" />
                Import Data
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show KYC wizard
  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-3xl mx-auto mb-6">
        <OnboardingProgress status={family.onboarding_status} className="mb-8" />
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold">{family.name}</h1>
        </div>
        <p className="text-muted-foreground">
          Complete the KYC information below. Your progress is saved automatically.
        </p>
      </div>

      <KYCWizard
        familyId={resolvedFamilyId!}
        orgId={resolvedOrgId!}
        familyName={family.name}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
    </div>
  );
}
